<template>
  <div id="assigned-studies-list">
    <div class="title-container row no-gutters">
      <div class="col-10 title">Assigned Studies</div>
      <div class="col-2 text-right">
        <button @click="assignStudies()" class="btn btn-outline-primary"><span class="fa fa-plus-circle mr-8"></span>Assign Study</button>
      </div>
    </div>
    <div class="assigned-studies-table-wrapper">
      <table class="table" aria-describedby="assigned-studies-list">
        <thead>
          <tr>
            <th :class="[field.class, field.key]" v-for="(field, index) in tableHeaderFields" :key="`header-${field.key}-${index}`">
              {{ field.label }}
              <div v-if="field.key == 'privileged'" id="privileged-column">
                <span class="fa fa-info-circle" id="privileged-popover"></span>
                <b-popover container="privileged-column" :target="`privileged-popover`" offset="90" triggers="hover" placement="leftbottom">
                  By default a user is set to privileged. This allows the user to view all personally identifiable information captured during the participant registration process in accordance with the study configuration. This information may include, but is not limited to: name, date of birth, gender, contact information, etc.
                  <br /><br />
                  <span class="bold-text">Toggling this permission off</span> will hide the personally identifiable information captured during the registration process from the user.
                </b-popover>
              </div>
            </th>
          </tr>
        </thead>
        <tbody data-testid="assign-studies-list" v-if="(assignedStudiesList && assignedStudiesList.length) || (userRoleHistory && userRoleHistory.length)">
          <tr v-for="(data, index) in assignedStudiesList" :key="`assigned-study-${data.studyId}-${index}`">
            <td class="study-name">{{ data.studyName }}</td>
            <td>
              {{ getSitesCount(data) }}
            </td>
            <td>
              <b-badge v-if="data.status.toLowerCase() == 'active'" variant="success">{{ data.status }}</b-badge>
              <b-badge v-else variant="danger">{{ data.status }}</b-badge>
            </td>
            <td class="text-center">
              <label :data-testid="`data-live-checkbox-${data.studyId}`" class="switch" :class="{ disableDataAccessChange: !canChangeLiveTestDataAccess }">
                <input type="checkbox" class="data-access-checkbox live-checkbox" v-model="data.liveDataAccess" value="Live" @change="changeLiveTestDataAccess($event, data)" />
                <div class="slider round"></div>
              </label>
            </td>
            <td class="text-center">
              <label :data-testid="`data-test-checkbox-${data.studyId}`" class="switch" :class="{ disableDataAccessChange: !canChangeLiveTestDataAccess }">
                <input type="checkbox" class="data-access-checkbox test-checkbox" v-model="data.testDataAccess" value="Test" @change="changeLiveTestDataAccess($event, data)" />
                <div class="slider round"></div>
              </label>
            </td>
            <td class="text-center">
              <label :data-testid="`privileged-checkbox-${data.studyId}`" class="switch">
                <input type="checkbox" v-model="data.blinded" @change="togglePrivilegedSetting(data)" :true-value="false" :false-value="true" />
                <div class="slider round"></div>
              </label>
            </td>
            <td class="text-center action-ellipse">
              <span class="option-dropdown" data-toggle="dropdown">
                <img src="static/img/activity-configurator/row-settings.svg" aria-haspopup="true" aria-expanded="false" alt />
                <ul class="dropdown-menu p-1">
                  <li :data-testid="`edit-assignment-${data.studyId}`" class="" @click="assignStudies(data)">
                    <span class="dropdown-item">Edit</span>
                  </li>
                  <li class="">
                    <span :data-testid="`toggle-study-status-${data.studyId}`" class="dropdown-item" @click="toggleStudyStatus(data)">
                      <span v-if="data.status == 'Active'">
                        Inactivate Assignment
                      </span>
                      <span v-if="data.status == 'Inactive'">
                        Activate Assignment
                      </span>
                    </span>
                  </li>
                </ul>
              </span>
            </td>
          </tr>
          <tr v-for="(historyData, index) in userRoleHistory" :key="`${historyData.id}-user-history-${index}`" v-bind:class="{ 'deactivated-record': historyData.status === 'Deactivated' }">
            <td class="study-name" :title="historyData.studyName">{{ historyData.studyName }}</td>
            <td></td>
            <td>
              <span class="deactivated">{{ historyData.status }}</span>
            </td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td :colspan="colspan">
              <div class="no-records">
                <div data-testId="no-assigned-studies" v-if="assignedStudiesList && assignedStudiesList.length == 0 && userRoleHistory && userRoleHistory.length == 0" class="text-center">
                  There are currently no studies assigned to this user. <br />
                  Click on the <span class="bold-font">Assign Study</span> button above to start assigning studies.
                </div>
                <div v-else class="dot-stretching"></div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <assignStudiesModal ref="assignStudiesModal" :userDetails="userDetails" :allStudies="allStudies" @assignmentsDone="reloadAssignments" />
  </div>
</template>

<script>
import assignStudiesModal from "@/components/users/assign-studies-modal";
export default {
  name: "assignedStudies",
  props: {
    userDetails: {
      type: Object,
      required: true
    },
    allStudies: {
      type: Array,
      required: true
    },
    userRoleHistory: {
      type: Array,
      required: true
    },
    canChangeLiveTestDataAccess: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      assignedStudiesList: this.userDetails.isSiteTeamUser ? this.userDetails.userSite : this.userDetails.userStudies ? this.userDetails.userStudies : [],
      tableHeaderFields: [
        {
          key: "study",
          label: "Study",
          class: ""
        },
        {
          key: "sites",
          label: "Sites",
          class: ""
        },
        {
          key: "assignmentStatus",
          label: "Assignment Status",
          class: ""
        },
        {
          key: "liveData",
          label: "Live Data",
          class: "text-center"
        },
        {
          key: "testData",
          label: "Test Data",
          class: "text-center"
        },
        {
          key: "privileged",
          label: "Privileged",
          class: "text-center"
        },
        {
          key: "action",
          label: "",
          class: ""
        }
      ],
      colspan: 8
    };
  },
  components: {
    assignStudiesModal
  },
  watch: {
    canChangeLiveTestDataAccess() {}
  },
  mounted() {
    this.setDataAccessTypes();
  },
  methods: {
    assignStudies(study = null) {
      this.$refs.assignStudiesModal.show(study);
    },
    setDataAccessTypes() {
      for (let study of this.assignedStudiesList) {
        this.$set(study, "liveDataAccess", false);
        this.$set(study, "testDataAccess", false);

        if (study.userDataAccessType == "LIVE") {
          study.liveDataAccess = true;
        } else if (study.userDataAccessType == "TEST") {
          study.testDataAccess = true;
        } else if (study.userDataAccessType == "BOTH") {
          study.liveDataAccess = true;
          study.testDataAccess = true;
        }
      }
    },
    changeLiveTestDataAccess(event, item) {
      if (!this.canChangeLiveTestDataAccess) {
        return;
      }

      const dataAccess = event.target.value;
      if (!item.liveDataAccess && !item.testDataAccess) {
        this.$toastr.error("Specifying data access rights is required.");
        if (dataAccess == "Test") {
          item.testDataAccess = true;
        } else {
          item.liveDataAccess = true;
        }
        return;
      }

      if (item.liveDataAccess && item.testDataAccess) {
        item.userDataAccessType = "BOTH";
      } else if (item.liveDataAccess && !item.testDataAccess) {
        item.userDataAccessType = "LIVE";
      } else if (!item.liveDataAccess && item.testDataAccess) {
        item.userDataAccessType = "TEST";
      }

      const onError = _error => {
        if (dataAccess === "Test") {
          item.testDataAccess = !item.testDataAccess;
        } else {
          item.liveDataAccess = !item.liveDataAccess;
        }
        this.$toastr.error(`Please try again later or contact your Study Administrator for further assistance.`, `${dataAccess} Data cannot be updated due to a technical issue.`);
      };

      this.$emit("dataAccessChangeChild", item, false, onError);
    },
    togglePrivilegedSetting(item) {
      const user = {
        studyId: item.studyId,
        userId: item.userId,
        siteId: item.siteId,
        blinded: item.blinded
      };
      this.$api.updateSiteUserBlindStatus(item.userId, item.studyId, item.siteId, item.blinded, user).then(
        () => {},
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while updating privileged status.");
          item.blinded = !item.blinded;
        }
      );
    },
    toggleStudyStatus(data) {
      let apiMethod, errorMsg;
      if (data.status == "Active") {
        apiMethod = "studyTeamDisassociateFact";
        errorMsg = `${data.studyName} has been unassigned from this user.`;
      } else {
        apiMethod = "studyTeamAssociateFact";
        errorMsg = `${data.studyName} has been assigned to this user.`;
      }

      this.$api[apiMethod](data.studyId, [this.userDetails.id]).then(
        () => {
          this.reloadAssignments();
          this.$toastr.success(errorMsg);
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while updating the assignment status.");
        }
      );
    },
    getSitesCount(data) {
      if (!data.assignedSiteIds) {
        return "";
      } else if (data.assignedSiteIds.length === 0) {
        //These are th ealready existing site prior to this TR02-408 implementation
        return `All Sites`;
      } else {
        //Find out for the All & enabled entry if found return all sites
        let allTypeAssignment = data.siteAssignments.find(d => d.assignmentType.toLowerCase() == "all" && d.isEnabled);
        if (allTypeAssignment) {
          return `All Sites`;
        } else {
          return `${data.assignedSiteIds.length} Sites`;
        }
      }
    },
    reloadAssignments() {
      this.$emit("reloadUserProfile");
    }
  }
};
</script>
<style scoped lang="scss" src="@/assets/dot-streaching-loader.scss"></style>
<style scoped lang="scss">
#assigned-studies-list {
  padding: 24px 24px 0 24px;
  background-color: #fff;
  margin-top: 25px;
  min-height: calc(100vh - 200px);

  .title-container {
    align-items: center;
    margin-bottom: 24px;

    .title {
      font-family: Roboto-Medium;
      font-size: 20px;
      text-align: left;
      color: #212b36;
    }
  }

  .assigned-studies-table-wrapper {
    padding-bottom: 48px;

    table {
      margin-bottom: 0;
      thead {
        tr {
          th {
            font-family: Roboto-Bold;
            font-size: 12px;
            color: #6d6e6b;
            border-bottom: 1px solid #e0e0e0;
            border-top: 1px solid #e0e0e0;
            text-transform: uppercase;
            padding: 20px;
            &.study {
              min-width: 246px;
            }

            .fa {
              font-size: 13px;
              &.fa-info-circle {
                color: #666669;
              }
            }
          }
        }
      }

      tbody {
        .no-records {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 224px;
          font-family: Roboto-Regular;
          color: #4a4a4a;
          font-size: 13px;

          .bold-font {
            font-family: Roboto-Bold;
          }
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          padding: 2rem 0;
          margin: 0 -5%;
          overflow: hidden;
        }

        tr {
          &:last-child {
            td {
              border-bottom: 1px solid #e0e0e0;
            }
          }
          &.deactivated-record {
            .study-name {
              color: #666669 !important;
              opacity: 0.5 !important;
            }
            .site-count {
              color: #666669 !important;
              opacity: 0.5 !important;
            }
          }
          .deactivated {
            color: #404040;
            background-color: #eee;
            padding: 2px 8px;
            border-radius: 6px;
            font-size: 12px;
            font-family: Roboto-Bold;
          }
          td {
            vertical-align: middle;
            padding: 20px;
            color: #212b36;
            border-top: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            &.study-name {
              max-width: 246px;
              min-width: 246px;
            }

            .badge {
              padding: 2px 8px;
              border-radius: 6px;
              font-size: 12px;
              text-transform: capitalize;
              line-height: inherit;
              &.badge-success {
                color: #1b806a;
                background-color: rgba(54, 179, 126, 0.16);
              }
              &.badge-danger {
                color: #b71d18;
                background-color: rgba(255, 86, 48, 0.16);
              }
            }

            .fa {
              &.fa-info-circle {
                color: #666669;
                vertical-align: middle;
              }
            }
            .edit {
              border-radius: 4px;
              padding: 8px;
              cursor: pointer;
              &:hover {
                background-color: rgba(176, 186, 201, 0.15);
              }
            }
            &.action-ellipse {
              padding: 0;
              .option-dropdown {
                cursor: pointer;
                padding: 5px 10px;
                &:hover {
                  background: rgba(176, 186, 201, 0.15);
                }
              }
              ul {
                line-height: 27px;
                font-size: 12px;
                padding: 0.125rem 0.75rem;
                box-shadow: 2px 2px 4px 4px rgba(4, 7, 0, 0.1);
                min-width: 7rem;
                li {
                  .dropdown-item {
                    padding-left: 12px !important;
                    font-size: 14px;
                    font-family: Roboto-Medium;
                    color: #666669;
                    &:hover {
                      background-color: #f3f5f7;
                    }
                    &:active {
                      background-color: #f3f5f7;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .btn-outline-primary {
    padding: 8px;
    border-radius: 4px;
    border: solid 1px #4c8ce4;
    cursor: pointer;
    font-family: Roboto-Bold;
    color: #4c8ce4;
    font-size: 13px;

    &:hover {
      background-color: rgba(76, 140, 228, 0.2);
    }
  }

  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 24px;
    margin-bottom: 0;
    &.disableDataAccessChange {
      cursor: none;
      pointer-events: none;
    }
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    border: 2px solid #666669;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    opacity: 0.4;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    -webkit-transition: 0.4s;
    background-color: #666669;
    transition: 0.4s;
  }

  input:checked + .slider {
    background-color: #fff;
    border: 2px solid #2ecc71;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #fff;
  }

  input:checked + .slider {
    opacity: 1;
  }

  input:checked + .slider:before {
    background-color: #2ecc71;
    -webkit-transform: translateX(16px);
    -ms-transform: translateX(16px);
    transform: translateX(16px);
  }

  /* Rounded sliders */
  .slider.round {
    border-radius: 24px;
  }

  .slider.round:before {
    border-radius: 50%;
  }
}
</style>
<style lang="scss">
#assigned-studies-list {
  #privileged-column {
    display: inline-block;
    .popover {
      width: 407px;
      max-width: 407px;
      background-color: #212b36;
      font-family: Roboto-Regular;
      font-size: 14px;
      border-radius: 8px;
      color: #ffffff;
      .arrow {
        &::after {
          bottom: 1px;
          border-left-color: #212b36;
        }
        &::before {
          bottom: 1px;
          right: 2px;
          border-left-color: #212b36;
        }
      }
      .popover-body {
        padding: 16px;
        color: #ffffff;
        .bold-text {
          font-family: Roboto-Bold;
        }
      }
    }
  }
}
#toast-container {
  .toast {
    &.toast-error {
      .toast-title {
        display: block !important;
      }
    }
  }
}
</style>
